import { useEffect, useRef, useState } from 'react';

/**
 * Minimum time the pick-time loading view stays on screen once shown, so a very
 * fast availability fetch (<200 ms) doesn't flash it (MPA-2580 acceptance
 * criteria). 150 ms is the threshold proposed on the ticket — change it here if
 * UX confirms a different value.
 */
export const PICK_TIME_LOADING_MIN_DISPLAY_MS = 150;

/**
 * Flicker guard for the pick-time loading view (MPA-2580, spec FR-009).
 *
 * Pass the availability fetch's pending flag; the returned flag drives whether
 * `PickTimeLoading` is rendered. It turns true the moment the fetch is pending
 * and stays true until both the fetch has settled and the view has been shown
 * for at least `minDisplayMs`. The pick-time container wires it up when the
 * loaded calendar view lands (MPA-2581).
 */
export const useMinimumDisplayTime = (
  isPending: boolean,
  minDisplayMs: number = PICK_TIME_LOADING_MIN_DISPLAY_MS,
): boolean => {
  const [isVisible, setIsVisible] = useState(isPending);
  const shownAtRef = useRef<number | null>(isPending ? Date.now() : null);

  useEffect(() => {
    if (isPending) {
      shownAtRef.current = Date.now();
      setIsVisible(true);
      return undefined;
    }

    if (!isVisible) {
      return undefined;
    }

    const elapsed = shownAtRef.current === null ? minDisplayMs : Date.now() - shownAtRef.current;
    const remaining = Math.max(minDisplayMs - elapsed, 0);

    if (remaining === 0) {
      setIsVisible(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setIsVisible(false), remaining);
    return () => window.clearTimeout(timer);
  }, [isPending, isVisible, minDisplayMs]);

  return isVisible;
};
